// VRG-734 — VERAfied labels, tones and filters. Pure (no React), so the guard can pin them.
import {
  VERAFIED_CONNECTION_LABELS,
  VERAFIED_CONNECTIONS,
  VERAFIED_EPISODE_STATUS_LABELS,
  VERAFIED_EPISODE_STATUSES,
  VERAFIED_EVALUATION_LABELS,
  VERAFIED_EVALUATIONS,
  VERAFIED_GUEST_STATUS_LABELS,
  VERAFIED_GUEST_STATUSES,
  VERAFIED_INFLUENCE,
  VERAFIED_INFLUENCE_LABELS,
  VERAFIED_LINK_STATE_LABELS,
  WEBSITE_INDUSTRY_LABELS,
  WEBSITE_INDUSTRY_SLUGS,
  WEBSITE_SOLUTION_LABELS,
  WEBSITE_SOLUTION_SLUGS,
  type VerafiedEpisodeStatus,
  type VerafiedEvaluation,
  type VerafiedGuestStatus,
  type VerafiedInfluence,
  type VerafiedLinkState,
} from '@repo/types'

import type { KanbanVerdict } from 'components/display/KanbanBoard'

import type { Tone } from '../../InternalAnalyticsPage/components/shared'
import type { VerafiedEpisode, VerafiedGuest } from 'services'

export const GUEST_STATUS_TONE: Readonly<Record<VerafiedGuestStatus, Tone>> = {
  not_invited: 'neutral',
  ready_to_invite: 'info',
  invited: 'info',
  accepted: 'positive',
  declined: 'negative',
  no_reply: 'caution',
  scheduled: 'positive',
  recorded: 'positive',
  published: 'positive',
  paused: 'caution',
}

export const INFLUENCE_TONE: Readonly<Record<VerafiedInfluence, Tone>> = { high: 'positive', medium: 'info', low: 'neutral' }

export const EVALUATION_TONE: Readonly<Record<VerafiedEvaluation, Tone>> = { love: 'positive', like: 'info', unsure: 'caution' }

export const LINK_STATE_TONE: Readonly<Record<VerafiedLinkState, Tone>> = {
  candidate: 'neutral',
  invited: 'info',
  confirmed: 'positive',
  recorded: 'positive',
}

const opts = <T extends string>(list: readonly T[], labels: Readonly<Record<T, string>>) => list.map((value) => ({ value, label: labels[value] }))

export const GUEST_STATUS_OPTIONS = opts(VERAFIED_GUEST_STATUSES, VERAFIED_GUEST_STATUS_LABELS)
export const INFLUENCE_OPTIONS = opts(VERAFIED_INFLUENCE, VERAFIED_INFLUENCE_LABELS)
export const CONNECTION_OPTIONS = opts(VERAFIED_CONNECTIONS, VERAFIED_CONNECTION_LABELS)
export const EVALUATION_OPTIONS = opts(VERAFIED_EVALUATIONS, VERAFIED_EVALUATION_LABELS)
export const EPISODE_STATUS_OPTIONS = opts(VERAFIED_EPISODE_STATUSES, VERAFIED_EPISODE_STATUS_LABELS)
export const SOLUTION_OPTIONS = opts(WEBSITE_SOLUTION_SLUGS, WEBSITE_SOLUTION_LABELS)
export const INDUSTRY_OPTIONS = opts(WEBSITE_INDUSTRY_SLUGS, WEBSITE_INDUSTRY_LABELS)
export const LINK_STATE_OPTIONS = opts(['candidate', 'invited', 'confirmed', 'recorded'] as const, VERAFIED_LINK_STATE_LABELS)

export const solutionLabel = (s: string) => (WEBSITE_SOLUTION_LABELS as Record<string, string>)[s] ?? s
export const industryLabel = (s: string) => (WEBSITE_INDUSTRY_LABELS as Record<string, string>)[s] ?? s

/** The board lanes: every status but Shelved, which is the collapsible strip. */
export const EPISODE_BOARD_LANES = VERAFIED_EPISODE_STATUSES.filter((s): s is Exclude<VerafiedEpisodeStatus, 'shelved'> => s !== 'shelved')

/**
 * The company line on a card. A guest whose company IS their own name (an artist, a band, a person
 * who is the brand) shows no company line: the sheet keeps it, the card does not repeat it.
 */
export const companyLine = (g: Pick<VerafiedGuest, 'name' | 'company_name' | 'company' | 'sales_account'>): string | null => {
  const name = g.company?.name ?? g.sales_account?.name ?? g.company_name
  if (!name) return null
  return name.trim().toLowerCase() === g.name.trim().toLowerCase() ? null : name
}

/** "Role · Company", dropping the company when the role already names it ("CEO at Jodie AI"). */
export const roleLine = (g: Pick<VerafiedGuest, 'name' | 'role_title' | 'company_name' | 'company' | 'sales_account'>): string => {
  const company = companyLine(g)
  const role = g.role_title?.trim() || null
  if (role && company && role.toLowerCase().includes(company.trim().toLowerCase())) return role
  return [role, company].filter(Boolean).join(' · ')
}

/** Initials for the avatar fallback: photos are uploaded, never scraped (spec §1). */
export const initialsOf = (name: string): string => {
  const words = name.replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean)
  if (!words.length) return '?'
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[words.length - 1][0]).toUpperCase()
}

export interface GuestFilters {
  q: string
  status: string[]
  influence: string[]
  connection: string[]
  /** '' = any, 'yes' / 'no'. */
  hasEpisode: '' | 'yes' | 'no'
  industry: string[]
  companyType: string[]
}

export const EMPTY_GUEST_FILTERS: GuestFilters = { q: '', status: [], influence: [], connection: [], hasEpisode: '', industry: [], companyType: [] }

const inList = (list: string[], v: string | null) => !list.length || (v !== null && list.includes(v))

/**
 * Search matches name, role, company and profile links, never notes: notes are sensitive, and a
 * search box that matches them would reveal their contents by elimination.
 */
export const filterGuests = (guests: readonly VerafiedGuest[], f: GuestFilters): VerafiedGuest[] => {
  const q = f.q.trim().toLowerCase()
  return guests.filter((g) => {
    if (!inList(f.status, g.status)) return false
    if (!inList(f.influence, g.influence)) return false
    if (!inList(f.connection, g.connection)) return false
    if (!inList(f.industry, g.industry)) return false
    if (!inList(f.companyType, g.company_type)) return false
    if (f.hasEpisode === 'yes' && !g.episodes.length) return false
    if (f.hasEpisode === 'no' && g.episodes.length) return false
    if (!q) return true
    const hay = [g.name, g.role_title, g.company_name, g.company?.name, g.sales_account?.name, g.lead?.email, ...g.links.map((l) => l.url)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return hay.includes(q)
  })
}

export interface EpisodeFilters {
  q: string
  evaluation: string[]
  status: string[]
  solution: string[]
  industry: string[]
  guest: string[]
}

export const EMPTY_EPISODE_FILTERS: EpisodeFilters = { q: '', evaluation: [], status: [], solution: [], industry: [], guest: [] }

/** `unrated` stands for a NULL evaluation in the filter. */
export const UNRATED = 'unrated'

export const filterEpisodes = (episodes: readonly VerafiedEpisode[], f: EpisodeFilters): VerafiedEpisode[] => {
  const q = f.q.trim().toLowerCase()
  return episodes.filter((e) => {
    if (f.evaluation.length && !f.evaluation.includes(e.evaluation ?? UNRATED)) return false
    if (!inList(f.status, e.status)) return false
    if (f.solution.length && !e.solutions.some((s) => f.solution.includes(s))) return false
    if (f.industry.length && !e.industries.some((s) => f.industry.includes(s))) return false
    if (f.guest.length && !e.guests.some((g) => f.guest.includes(g.id))) return false
    if (!q) return true
    return `${e.title} ${e.description ?? ''}`.toLowerCase().includes(q)
  })
}

/**
 * What may move onto a board lane. Two rules, both about going public: an episode cannot be
 * marked Published without the /insights slug it was published at, nor while its title still
 * carries a figure nobody has checked (spec §5, the stat registry rule).
 */
export const episodeMoveVerdict = (
  e: Pick<VerafiedEpisode, 'insights_slug' | 'claims_check'>,
  to: VerafiedEpisodeStatus,
): KanbanVerdict => {
  if (to !== 'published') return { kind: 'ok' }
  if (e.claims_check) return { kind: 'refused', reason: 'Resolve the claims check first' }
  if (!e.insights_slug) return { kind: 'refused', reason: 'Add the /insights slug it was published at' }
  return { kind: 'ok' }
}

/** "12:30" from seconds; null when unknown. */
export const lengthLabel = (seconds: number | null): string | null => {
  if (seconds == null) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export const parseLength = (v: string): number | null | 'invalid' => {
  const t = v.trim()
  if (!t) return null
  const m = /^(\d{1,3})(?::([0-5]\d))?$/.exec(t)
  if (!m) return 'invalid'
  return Number(m[1]) * 60 + Number(m[2] ?? 0)
}
